import React, { createContext, useContext, useEffect, useState } from "react";
import { api, formatApiError, getAccessToken, isAccessTokenExpired, setAccessToken } from "./api";

const AuthContext = createContext({ user: null, loading: true, login: async () => {}, logout: async () => {} });

async function restoreSession() {
  const token = getAccessToken();
  if (!token) return null;

  if (!isAccessTokenExpired(token)) {
    try {
      const { data } = await api.get("/auth/me");
      return data;
    } catch (e) {
      const status = e?.response?.status;
      if (!status) throw e;
      if (status !== 401) throw e;
    }
  }

  try {
    const { data: refreshed } = await api.post("/auth/refresh");
    if (!refreshed?.token) return null;
    setAccessToken(refreshed.token);
    const { data } = await api.get("/auth/me");
    return data;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await restoreSession();
        if (data) setUser(data);
        else {
          setUser(null);
          setAccessToken(null);
        }
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data?.token) setAccessToken(data.token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e?.response?.data?.detail) || e?.message || "Erro ao entrar" };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setAccessToken(null);
    setUser(null);
  };

  return <AuthContext.Provider value={{ user, loading, login, logout, setUser }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
